import { useMemo } from 'react';
import { Formik, FormikConfig } from 'formik';
import { IWorkflow } from '@modules/admin/AdminTools/Workflows/types';
import { Modal } from '@shared/components/modals/Modal';
import {
  IWorkflowCommunication,
  TShortWorkflowStep,
  ICommunicationValues,
  IEmail,
  ISid,
  IEmailTemplates,
  ISMSTemplates,
} from '../types';
import { customerCommunicationSchema } from '../validation';
import { EditCommunicationForm } from './EditCommunicationForm';

const DEFAULT_DELAY = 5;

const getWorkflowIds = (
  workflowStepsValue: string[],
  workflowSteps: TShortWorkflowStep[],
  workflowTree: IWorkflow[],
): (string | null)[] =>
  !workflowStepsValue.length
    ? [null]
    : workflowStepsValue.map((stepId) => {
        const currentStep = workflowSteps.find(({ id }) => id === stepId);
        const currentWorkflow = workflowTree.find(({ workflowId }) => workflowId === currentStep?.workflowId);
        return currentWorkflow?.workflowId ?? null;
      });

interface IProps {
  emails: IEmail[];
  sids: ISid[];
  emailError: any;
  sidsError: any;
  emailTemplates: IEmailTemplates;
  smsTemplates: ISMSTemplates;
  editCommunicationModalPosition: number;
  currentCommunication: IWorkflowCommunication | null;
  workflowSteps: TShortWorkflowStep[];
  workflowTree: IWorkflow[];
  onClose: () => void;
  onSubmit: FormikConfig<ICommunicationValues>['onSubmit'];
}

export const EditCommunicationModal = ({
  emails,
  sids,
  emailTemplates,
  smsTemplates,
  emailError,
  sidsError,
  editCommunicationModalPosition,
  currentCommunication,
  workflowSteps,
  workflowTree,
  onClose,
  onSubmit,
}: IProps) => {
  const initialValues = useMemo<ICommunicationValues>(
    () => ({
      id: currentCommunication?.id,
      isEmail: currentCommunication?.isEmail ?? false,
      isSms: currentCommunication?.isSms ?? false,
      isAdverseAction: currentCommunication?.isAdverseAction ?? false,
      emailTemplateId: currentCommunication?.emailTemplateId ?? '',
      emailSubject: '',
      emailTemplate:  {
        html: '',
        plainText: '',
      },
      smsTemplate: '',
      smsTemplateId: currentCommunication?.smsTemplateId ?? '',
      fromEmail: currentCommunication?.fromEmail ?? null,
      fromSid: currentCommunication?.fromSid ?? null,
      isSafeToContact: currentCommunication?.isSafeToContact ?? false,
      delayInMinutes: currentCommunication?.delayInMinutes ?? DEFAULT_DELAY,
      excludedStates: currentCommunication?.excludedStates ?? [],
      includedLenders: currentCommunication?.includedLenders ?? [],
      includedLeadSources: currentCommunication?.includedLeadSources ?? [],
      workflowSteps: currentCommunication?.workflowSteps ?? [null],
      workflowIds: getWorkflowIds(currentCommunication?.workflowSteps ?? [], workflowSteps, workflowTree),
      selectedEmailTemplate: null,
      selectedSMSTemplate:null
    }),
    [currentCommunication, workflowSteps, workflowTree]
  );

  return (
    <Modal
      hideBackdrop
      title="Workflow Step Message"
      open={!!editCommunicationModalPosition}
      resetPosition={editCommunicationModalPosition}
      dialogWidth="100%"
      dialogMaxWidth="1050px"
      onClose={onClose}
    >
      <Formik
        validateOnChange
        initialValues={initialValues}
        validationSchema={customerCommunicationSchema}
        onSubmit={onSubmit}
        onReset={onClose}
      >
        <EditCommunicationForm
          workflowTree={workflowTree}
          isSidLoadedWithError={sidsError}
          isEmailLoadedWithError={emailError}
          sids={sids || []}
          emails={emails || []}
          emailTemplates={emailTemplates || []}
          smsTemplates={smsTemplates || []}
        />
      </Formik>
    </Modal>
  );
};